import { useState } from "react";
import classNames from "classnames/bind";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { buildColourSwatchUrl } from "@/components/Colours/ColoursUI";
import { Colour } from "./Colour";
import styles from "./ColourList.module.scss";

const cx = classNames.bind(styles);

export type ColourListOption = {
  colour: string;
  showAsAvailable: boolean;
  colourSwatch: string | null | undefined;
  selected: boolean;
  imageUrl?: string;
};

type ColourListProps = {
  colourOptions: ColourListOption[];
  showMore?: number;
  selectedLabel?: string | null;
  onClick: (value: string) => void;
};

type SelectorLabelProps = {
  label: string;
  selectedLabel: string;
};

export const ColourList = ({
  colourOptions,
  showMore = 6,
  selectedLabel,
  onClick,
}: ColourListProps) => {
  const [isShowingMore, setIsShowingMore] = useState(false);

  if (!colourOptions || colourOptions.length === 0) {
    return null;
  }

  const labelText = "Colour";

  const numberHiddenControls = colourOptions.length - showMore;

  const canShowMore = colourOptions.length > showMore;

  const renderedLabel = selectedLabel ? (
    <SelectorLabel label={labelText} selectedLabel={selectedLabel} />
  ) : (
    <h4 className={styles.colourList__title} data-testid="colourlist:label">
      {labelText}
    </h4>
  );

  return (
    <div
      className={styles.colourList}
      data-component="Colours"
      data-jlds={true}
    >
      {renderedLabel}
      <ul
        className={cx(
          styles.colourList__items,
          styles.colourList__items__three,
        )}
      >
        {colourOptions.map((colourOption, index) => {
          if (isShowingMore || (!isShowingMore && index < showMore)) {
            return (
              <li key={colourOption.colour}>
                <Colour
                  selected={colourOption.selected}
                  unavailable={!colourOption.showAsAvailable}
                  value={colourOption.colour}
                  swatch={buildColourSwatchUrl(colourOption.colourSwatch)}
                  onClick={onClick}
                />
              </li>
            );
          }
          return null;
        })}
      </ul>
      {canShowMore && (
        <div className={styles.showMoreContainer}>
          <TertiaryButton
            className={styles.showMoreButton}
            icon={isShowingMore ? MinusOutlined : PlusOutlined}
            labelText={
              isShowingMore ? "Show less" : `Show ${numberHiddenControls} more`
            }
            onClick={() => {
              setIsShowingMore(!isShowingMore);
            }}
          />
        </div>
      )}
    </div>
  );
};

const SelectorLabel = ({
  label = "Colour",
  selectedLabel,
}: SelectorLabelProps) => (
  <h4 className={styles.colourList__title} data-testid="colourlist:label">
    <span className={styles.colourList__label}>{`${label}: `}</span>
    <span className={styles.colourList__selected}>{selectedLabel}</span>
  </h4>
);
